import { INSTRUMENTS } from '../lib/instruments';
import { useSelection } from '../lib/selection';
import { api } from '../lib/api';
import { useAsync } from '../lib/dataHooks';
import type { ViewId } from '../lib/app';
import { useEffect, useState } from 'react';
import type { CalendarCell } from '../lib/api';

/**
 * 复盘视图 —— 接入 /api/calendar 与 /api/kline（需求 §F4）
 *
 * 三个模块：
 *  · 月历热力图：交易日按当日涨跌着色，点新闻数角标
 *  · 情绪时间线：把已写笔记按日期连成折线，看判断与实际的偏差
 *  · 快捷跳转：点任意一天直接定位到该日复盘
 *
 * 关键：复盘的入口是**日期**，故点日历格子后必须同时设置 date 与切到图表页，
 * 否则「看复盘」会落在最新一天上。
 */
export function ReviewView({ onNavigate }: { onNavigate: (v: ViewId) => void }) {
  const { instrument, select, setDate, effectiveDate, latestTradingDay } = useSelection();

  /**
   * 默认月份取**最新交易日所在月**，而非当前自然月。
   *
   * 现状是 10-08，而多数 A股指数的最新K线是 09-30（国庆休市）——
   * 若默认显示 10 月，整月热力图会是空的（0 交易日），用户以为功能坏了。
   * 月份切换按钮仍可自由查看其他月份。
   */
  const anchor = latestTradingDay ?? new Date().toISOString().slice(0, 10);
  const [cursor, setCursor] = useState(`${anchor.slice(0, 7)}`);
  const [monthTouched, setTouched] = useState(false);

  // 指数切换或日历就绪后，把视图对齐到该指数的最新交易日（仅在用户未手动翻月时）
  useEffect(() => {
    if (!monthTouched && anchor) setCursor(anchor.slice(0, 7));
  }, [anchor, monthTouched]);

  const year = Number(cursor.slice(0, 4));
  const month = Number(cursor.slice(5, 7));

  const from = `${cursor}-01`;
  const to = lastDayOfMonth(year, month);

  const calQ = useAsync(() => api.calendar(instrument.code, from, to), [instrument.code, from, to]);
  const klineQ = useAsync(() => api.kline(instrument.code, 'D', 400), [instrument.code]);
  // 当月笔记清单：区间即所显示月（此前上界误传 from=月初，
  // 结果显示的是上个月及更早，本月 1 号之后的反而看不见）
  const notesQ = useAsync(
    () => api.notes(instrument.code, from, to), [instrument.code, from, to]);
  /** 预测（情绪每天一条）—— 情绪时间轴改读它，不再从笔记里取 */
  const predQ = useAsync(
    () => api.predictions(instrument.code, from, to), [instrument.code, from, to]);

  /** 日期 → 涨跌幅 */
  const pctByDate = new Map<string, number>(
    (klineQ.data?.bars ?? []).map((b, i, arr) => {
      const prev = i > 0 ? arr[i - 1]!.close : b.open;
      return [b.date, prev > 0 ? ((b.close - prev) / prev) * 100 : 0] as const;
    }),
  );
  const cells = calQ.data?.items ?? [];

  const sentScore: Record<string, number> = { bullish: 1, bearish: -1, neutral: 0 };

  const jump = (date: string) => {
    setDate(date);
    onNavigate('chart');
  };

  /** 仅选中日期（不跳转）—— 月历格点击用；跳转见下方「跳转图表」按钮 */
  const pickDate = (date: string) => setDate(date);

  const monthStats = summarize(cells, pctByDate);

  return (
    <div className="flex flex-col p-3">
      <div className="mb-3 flex items-center gap-2">
        <h1 className="text-[14px] font-semibold">复盘</h1>
        <select
          value={instrument.code}
          onChange={(e) => select(e.target.value)}
          className="rounded border px-2 py-1 text-[12px]"
          style={{ background: 'var(--bg-surface)', color: 'var(--text-1)' }}
        >
          {INSTRUMENTS.map((i) => (
            <option key={i.code} value={i.code}>{i.name}</option>
          ))}
        </select>

        {/* 时间筛选紧跟指数选择之后（此前挤在最右侧） */}
        <span className="flex items-center gap-1 text-[12px]" style={{ color: 'var(--text-3)' }}>
          <button type="button" onClick={() => shiftMonth(-1)}
            className="rounded px-1.5 py-0.5" style={{ color: 'var(--text-2)' }}>‹</button>
          <span className="num">{year} 年 {month} 月</span>
          <button type="button" onClick={() => shiftMonth(1)}
            className="rounded px-1.5 py-0.5" style={{ color: 'var(--text-2)' }}>›</button>
        </span>

        {/* 跳转入口收在顶栏（原在当日快照区标题右侧）：落到图表页的选中日 */}
        {effectiveDate && (
          <button type="button" onClick={() => onNavigate('chart')}
            className="rounded border px-2 py-0.5 text-[11px]"
            style={{ borderColor: 'var(--border-strong)', color: 'var(--accent)' }}>
            跳转图表 →
          </button>
        )}
      </div>

      <div className="flex flex-col gap-3 lg:flex-row">
        {/* 月历热力图 */}
        <section className="rounded-[10px] border p-3 lg:flex-1"
          style={{ background: 'var(--bg-surface)', boxShadow: 'var(--shadow-card)' }}>
          <div className="mb-2 flex items-baseline gap-3">
            <h2 className="text-[13px] font-medium">{year} 年 {month} 月</h2>
            <span className="num text-[11px]" style={{ color: 'var(--text-3)' }}>
              {monthStats.tradingDays} 交易日 · 均 {monthStats.avgPct.toFixed(2)}% ·
              胜率 {(monthStats.winRate * 100).toFixed(0)}%
            </span>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center">
            {['一', '二', '三', '四', '五', '六', '日'].map((w) => (
              <div key={w} className="py-1 text-[11px]" style={{ color: 'var(--text-3)' }}>{w}</div>
            ))}
            {padLeading(cells).map((cell, i) => {
              if (!cell) return <div key={`pad${i}`} />;
              const pct = pctByDate.get(cell.date);
              const isSel = cell.date === effectiveDate;
              const bg = !cell.isTradingDay
                ? 'var(--bg-elevated)'
                : pct === undefined ? 'var(--bg-inset)'
                : heatColor(pct);

              return (
                <button
                  key={cell.date}
                  type="button"
                  // 仅选中日期，不跳页 —— 跳转由顶栏的「跳转图表」按钮负责
                  onClick={() => cell.reviewable && pickDate(cell.date)}
                  disabled={!cell.reviewable}
                  className="relative grid aspect-square place-items-center rounded-[3px] text-[11px] num transition-transform"
                  style={{
                    background: bg,
                    opacity: cell.isTradingDay ? 1 : 0.4,
                    color: cell.isTradingDay ? '#fff' : 'var(--text-3)',
                    boxShadow: isSel ? 'inset 0 0 0 2px var(--accent)' : undefined,
                    cursor: cell.reviewable ? 'pointer' : 'default',
                  }}
                  title={
                    cell.isTradingDay
                      ? `${cell.date}${pct !== undefined ? ` ${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%` : ''}` +
                        `${cell.starCount ? ` · 星标新闻 ${cell.starCount} 条` : ''}` +
                        `${cell.noteCount ? ' · 有笔记' : ''}` +
                        `${cell.predCount ? ' · 有预测' : ''}` +
                        `${cell.hasMinute ? '' : ' · 无分时'}`
                      : `${cell.date} 非交易日`
                  }
                >
                  {Number(cell.date.slice(8, 10))}
                  {/* 右上金点 = 当日有**标星**新闻（不看 newsCount 全量） */}
                  {cell.starCount > 0 && (
                    <span className="absolute right-0.5 top-0.5 h-1 w-1 rounded-full"
                      style={{ background: 'var(--star)' }} aria-hidden />
                  )}
                  {/* 垂直居中紫点 = 当日写了预测 */}
                  {cell.predCount > 0 && (
                    <span className="absolute right-0.5 top-1/2 h-1 w-1 -translate-y-1/2 rounded-full"
                      style={{ background: 'var(--pred)' }} aria-hidden />
                  )}
                  {/* 右下蓝点 = 当日有笔记（真正的 notes，不是预测） */}
                  {cell.noteCount > 0 && (
                    <span className="absolute bottom-0.5 right-0.5 h-1 w-1 rounded-full"
                      style={{ background: 'var(--accent)' }} aria-hidden />
                  )}
                  {/* 内侧下方涨跌幅标：底色已编码涨跌，白字避免红/绿叠红/绿底不可读；
                      居中放置，与右下角蓝点（角落钉着）错开 */}
                  {cell.isTradingDay && pct !== undefined && (
                    <span className="absolute inset-x-0 bottom-0 text-[9px] font-medium leading-none text-white/90 num">
                      {pct >= 0 ? '+' : ''}{pct.toFixed(2)}%
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="mt-2 flex items-center gap-2 text-[10px]" style={{ color: 'var(--text-3)' }}>
            <span>跌</span>
            {[-3, -2, -1, 0, 1, 2, 3].map((v) => (
              <span key={v} className="h-2.5 w-4 rounded-[2px]"
                style={{ background: heatColor(v) }} />
            ))}
            <span>涨</span>
            <span className="ml-2 flex items-center gap-1"><Dot c="var(--star)" />星标新闻</span>
            <span className="flex items-center gap-1"><Dot c="var(--pred)" />预测</span>
            <span className="flex items-center gap-1"><Dot c="var(--accent)" />笔记</span>
          </div>
        </section>

        {/* 情绪时间线 */}
        <section className="rounded-[10px] border p-3 lg:w-[300px]"
          style={{ background: 'var(--bg-surface)', boxShadow: 'var(--shadow-card)' }}>
          <h2 className="mb-2 text-[13px] font-medium">情绪时间线</h2>
          <SentimentTimeline
            predictions={predQ.data?.items ?? []}
            score={sentScore}
          />
        </section>

        {/* 当月笔记清单 */}
        <section className="rounded-[10px] border p-3 lg:w-[280px]"
          style={{ background: 'var(--bg-surface)', boxShadow: 'var(--shadow-card)' }}>
          <h2 className="mb-2 text-[13px] font-medium">当月笔记 ({notesQ.data?.items.length ?? 0})</h2>
          <div className="max-h-[420px] overflow-y-auto">
            {(notesQ.data?.items ?? []).slice(0, 40).map((n) => (
              <button key={n.id} type="button"
                onClick={() => jump(n.scope === 'day' ? n.scopeKey : effectiveDate ?? '')}
                className="block w-full border-b px-1 py-1.5 text-left last:border-b-0"
                style={{ color: 'var(--text-1)' }}>
                <div className="num flex items-center gap-2 text-[11px]" style={{ color: 'var(--text-3)' }}>
                  <span>{n.scopeKey}</span>
                </div>
                <div className="line-clamp-2 text-[12px] leading-[1.4]">
                  {n.content.replace(/[#*`>-]/g, '').trim() || '（空）'}
                </div>
              </button>
            ))}
            {notesQ.data?.items.length === 0 && (
              <div className="py-3 text-[12px]" style={{ color: 'var(--text-3)' }}>
                本月尚无笔记
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );

  function shiftMonth(n: number) {
    setTouched(true);
    const [y, m] = cursor.split('-').map(Number) as [number, number];
    const d = new Date(Date.UTC(y, m - 1 + n, 1));
    setCursor(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
}

function SentimentTimeline({
  predictions, score,
}: {
  predictions: { date: string; sentiment: string | null }[];
  score: Record<string, number>;
}) {
  const withS = predictions
    .filter((n) => n.sentiment)
    .sort((a, b) => a.date.localeCompare(b.date));

  if (withS.length === 0) {
    return (
      <div className="py-3 text-[12px]" style={{ color: 'var(--text-3)' }}>
        尚无带情绪标记的预测。
        <br />在工作区的「预测」里选择「看多/看空/中性」后会出现在这里。
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {withS.slice(-20).reverse().map((n) => {
        const v = score[n.sentiment ?? 'neutral'] ?? 0;
        const color = v > 0 ? 'var(--up)' : v < 0 ? 'var(--down)' : 'var(--text-3)';
        return (
          <div key={n.date} className="flex items-center gap-2">
            <span className="num shrink-0 text-[11px]" style={{ color: 'var(--text-3)' }}>
              {n.date.slice(5)}
            </span>
            <span className="shrink-0 text-[11px]" style={{ color }}>
              {n.sentiment === 'bullish' ? '● 看多' : n.sentiment === 'bearish' ? '● 看空' : '○ 中性'}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ─────────────── helper ─────────────── */

/** 图例里的小圆点（与格子上的标记同色同形） */
function Dot({ c }: { c: string }) {
  return <span className="h-1 w-1 rounded-full" style={{ background: c }} aria-hidden />;
}

function heatColor(pct: number): string {
  const a = Math.min(0.92, 0.14 + Math.abs(pct) / 3);
  return pct >= 0
    ? `rgba(241, 71, 77, ${a.toFixed(3)})`
    : `rgba(20, 177, 67, ${a.toFixed(3)})`;
}

/** 周一为首列，补齐月首空白 */
function padLeading(cells: CalendarCell[]): (CalendarCell | null)[] {
  const first = cells[0];
  if (!first) return [];
  const dow = new Date(`${first.date}T00:00:00Z`).getUTCDay();
  const lead = dow === 0 ? 6 : dow - 1;
  return [...Array<null>(lead).fill(null), ...cells];
}

function summarize(cells: CalendarCell[], pct: Map<string, number>): { tradingDays: number; avgPct: number; winRate: number } {
  const vals = cells
    .filter((c) => c.hasKline && pct.has(c.date))
    .map((c) => pct.get(c.date)!);
  if (vals.length === 0) return { tradingDays: 0, avgPct: 0, winRate: 0 };
  return {
    tradingDays: vals.length,
    avgPct: vals.reduce((a, b) => a + b, 0) / vals.length,
    winRate: vals.filter((v) => v > 0).length / vals.length,
  };
}

function lastDayOfMonth(y: number, m: number): string {
  return `${y}-${String(m).padStart(2, '0')}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, '0')}`;
}